import { useEffect, useMemo, useState } from 'react';
import { Chip, Grid, Link, makeStyles, Typography } from '@material-ui/core';
import { EmptyState, InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  Position,
  ReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

type ArchitectureNode = {
  id: string;
  label: string;
  tech?: string;
  env?: string;
  group?: string;
  x?: number;
  y?: number;
};

type ArchitectureEdge = {
  id?: string;
  source: string;
  target: string;
  protocol?: string;
  relation?: string;
};

type ArchitectureGroup = {
  id: string;
  label: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  color?: string;
};

type ArchitectureAnnotations = {
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  groups: ArchitectureGroup[];
};

type ParsedGiteaDiagram = {
  owner: string;
  repo: string;
  ref: string;
  filePath: string;
};

const ANNOTATION_NODES = 'amfs.io/architecture-nodes';
const ANNOTATION_EDGES = 'amfs.io/architecture-edges';
const ANNOTATION_GROUPS = 'amfs.io/architecture-groups';
const ANNOTATION_IMAGE = 'amfs.io/architecture-diagram-url';
const ANNOTATION_MODE = 'amfs.io/architecture-mode';

const DEFAULT_PROTOCOL = 'REL';

const RELATION_PROTOCOLS: Record<string, string> = {
  dependsOn: 'REST',
  dependencyOf: 'REST',
  consumesApi: 'REST',
  apiConsumedBy: 'REST',
  providesApi: 'REST',
  apiProvidedBy: 'REST',
  partOf: 'PART_OF',
  hasPart: 'HAS_PART',
};

const NODE_WIDTH = 220;
const GITEA_PROXY_PREFIX = '/api/proxy/gitea';

const TECH_COLORS: Record<string, string> = {
  service: '#2563eb',
  api: '#0284c7',
  database: '#0f766e',
  queue: '#7c2d12',
  cache: '#7c3aed',
  ui: '#b45309',
  gateway: '#be123c',
  external: '#475569',
};

const useStyles = makeStyles(theme => ({
  flowRoot: {
    width: '100%',
    height: 700,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    background: '#0f172a',
  },
  imageWrap: {
    width: '100%',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    background: '#ffffff',
  },
  diagramImage: {
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
  },
  legendRow: {
    marginBottom: theme.spacing(1),
  },
  legendChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  groupTitle: {
    fontWeight: 700,
    color: '#ffffff',
  },
  nodeLabel: {
    color: '#0f172a',
    fontWeight: 700,
  },
  nodeMeta: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  techPill: {
    background: '#1e293b',
    color: '#ffffff',
    padding: '2px 6px',
    borderRadius: 12,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
  },
  envPill: {
    color: '#0f172a',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
  },
}));

const parseJsonArray = <T,>(raw: string | undefined): T[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseGiteaDiagramUrl = (
  rawUrl: string | undefined,
): ParsedGiteaDiagram | undefined => {
  if (!rawUrl) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(rawUrl);

    if (parsedUrl.hostname !== 'git.amfs-sandbox.com') {
      return undefined;
    }

    const srcMatch = parsedUrl.pathname.match(
      /^\/([^/]+)\/([^/]+)\/src\/branch\/([^/]+)\/(.+)$/,
    );

    if (srcMatch) {
      const [, owner, repo, ref, filePath] = srcMatch;
      return { owner, repo, ref, filePath };
    }

    const rawMatch = parsedUrl.pathname.match(
      /^\/([^/]+)\/([^/]+)\/raw\/branch\/([^/]+)\/(.+)$/,
    );

    if (rawMatch) {
      const [, owner, repo, ref, filePath] = rawMatch;
      return { owner, repo, ref, filePath };
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const toProxiedDiagramUrl = (rawUrl: string | undefined): string | undefined => {
  if (!rawUrl) {
    return undefined;
  }

  const giteaDiagram = parseGiteaDiagramUrl(rawUrl);
  if (!giteaDiagram) {
    return rawUrl;
  }

  const encodedPath = giteaDiagram.filePath
    .split('/')
    .map(segment => encodeURIComponent(segment))
    .join('/');

  return `${GITEA_PROXY_PREFIX}/repos/${encodeURIComponent(
    giteaDiagram.owner,
  )}/${encodeURIComponent(giteaDiagram.repo)}/raw/${encodedPath}?ref=${encodeURIComponent(
    giteaDiagram.ref,
  )}`;
};

const relationLabel = (relationType: string): string => {
  return RELATION_PROTOCOLS[relationType] ?? DEFAULT_PROTOCOL;
};

const buildFlow = (
  annotations: ArchitectureAnnotations,
  entityName: string,
  classes: ReturnType<typeof useStyles>,
): { nodes: Node[]; edges: Edge[]; protocols: string[] } => {
  const groupsById = new Map(annotations.groups.map(group => [group.id, group]));

  const flowNodes: Node[] = annotations.groups.map(group => ({
    id: group.id,
    type: 'default',
    position: { x: group.x ?? 0, y: group.y ?? 0 },
    draggable: false,
    selectable: false,
    data: {
      label: (
        <Typography variant="subtitle2" className={classes.groupTitle}>
          {group.label}
        </Typography>
      ),
    },
    style: {
      width: group.width ?? 360,
      height: group.height ?? 260,
      borderRadius: 12,
      border: `2px solid ${group.color ?? '#334155'}`,
      background: 'rgba(15, 23, 42, 0.55)',
      color: '#ffffff',
      padding: 10,
      zIndex: 0,
    },
  }));

  annotations.nodes.forEach((node, index) => {
    const techKey = (node.tech ?? 'service').toLowerCase();
    const color = TECH_COLORS[techKey] ?? '#1f2937';

    const parentGroup = node.group ? groupsById.get(node.group) : undefined;
    const x = node.x ?? (index % 3) * 170 + 30;
    const y = node.y ?? Math.floor(index / 3) * 115 + 50;
    const sourcePosition =
      node.group === 'external-systems'
        ? Position.Right
        : node.group === 'shared-platform'
          ? Position.Left
          : Position.Right;
    const targetPosition =
      node.group === 'external-systems'
        ? Position.Right
        : node.group === 'shared-platform'
          ? Position.Left
          : Position.Left;

    flowNodes.push({
      id: node.id,
      type: 'default',
      position: { x, y },
      parentId: parentGroup?.id,
      extent: parentGroup ? 'parent' : undefined,
      sourcePosition,
      targetPosition,
      data: {
        label: (
          <div>
            <div className={classes.nodeLabel}>{node.label || entityName}</div>
            <div className={classes.nodeMeta}>
              <span className={classes.techPill}>
                {(node.tech ?? 'service').toUpperCase()}
              </span>
              <span className={classes.envPill}>
                {(node.env ?? 'dev').toUpperCase()}
              </span>
            </div>
          </div>
        ),
      },
      style: {
        border: `2px solid ${color}`,
        borderRadius: 10,
        padding: 12,
        minWidth: NODE_WIDTH,
        background: '#f8fafc',
      },
    });
  });

  const protocolSet = new Set<string>();

  const flowEdges: Edge[] = annotations.edges.map((edge, index) => {
    const protocol = (edge.protocol ?? relationLabel(edge.relation ?? '')).toUpperCase();
    protocolSet.add(protocol);

    return {
      id: edge.id || `edge-${index}`,
      source: edge.source,
      target: edge.target,
      label: protocol,
      type: 'smoothstep',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
      },
      labelStyle: {
        fill: '#ffffff',
        fontSize: 10,
        fontWeight: 700,
      },
      labelBgStyle: {
        fill: '#0f172a',
        fillOpacity: 0.86,
      },
      style: {
        stroke: '#cbd5e1',
        strokeWidth: 1.8,
      },
    };
  });

  return {
    nodes: flowNodes,
    edges: flowEdges,
    protocols: Array.from(protocolSet),
  };
};

export const ArchitectureDiagramCard = ({
  title = 'High Level Architecture',
}: {
  title?: string;
}) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const architectureImageUrl = annotations[ANNOTATION_IMAGE];
  const renderedImageUrl = useMemo(
    () => toProxiedDiagramUrl(architectureImageUrl),
    [architectureImageUrl],
  );
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | undefined>();
  const [imageLoadError, setImageLoadError] = useState<string | undefined>();

  useEffect(() => {
    let isActive = true;
    let objectUrl: string | undefined;

    const loadImage = async () => {
      setImageLoadError(undefined);

      if (!renderedImageUrl) {
        setResolvedImageUrl(undefined);
        return;
      }

      if (!renderedImageUrl.startsWith('/api/proxy/')) {
        setResolvedImageUrl(renderedImageUrl);
        return;
      }

      try {
        const response = await fetchApi.fetch(renderedImageUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const imageBlob = await response.blob();
        objectUrl = window.URL.createObjectURL(imageBlob);

        if (isActive) {
          setResolvedImageUrl(objectUrl);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        setResolvedImageUrl(undefined);
        setImageLoadError(
          error instanceof Error ? error.message : 'Unable to load image',
        );
      }
    };

    loadImage();

    return () => {
      isActive = false;

      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fetchApi, renderedImageUrl]);

  const annotationGraph = useMemo<ArchitectureAnnotations>(() => {
    const nodes = parseJsonArray<ArchitectureNode>(annotations[ANNOTATION_NODES]);
    const edges = parseJsonArray<ArchitectureEdge>(annotations[ANNOTATION_EDGES]);
    const groups = parseJsonArray<ArchitectureGroup>(annotations[ANNOTATION_GROUPS]);

    return {
      nodes,
      edges,
      groups,
    };
  }, [annotations]);

  const hasAnnotationGraph =
    annotationGraph.nodes.length > 0 && annotationGraph.edges.length > 0;
  const isManualMode = (annotations[ANNOTATION_MODE] || '').toLowerCase() === 'manual';
  const shouldRenderManualGraph = isManualMode && hasAnnotationGraph;

  const manualFlow = useMemo(() => {
    if (!shouldRenderManualGraph) {
      return {
        nodes: [],
        edges: [],
        protocols: [],
      };
    }

    return buildFlow(annotationGraph, entity.metadata.name, classes);
  }, [annotationGraph, classes, entity.metadata.name, shouldRenderManualGraph]);

  if (architectureImageUrl) {
    return (
      <InfoCard title={title}>
        <Grid container>
          <Grid item xs={12} className={classes.legendRow}>
            <Link href={architectureImageUrl} target="_blank" rel="noopener noreferrer">
              Open Source PNG
            </Link>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.imageWrap}>
              {resolvedImageUrl ? (
                <img
                  src={resolvedImageUrl}
                  alt={`${entity.metadata.name} architecture diagram`}
                  className={classes.diagramImage}
                />
              ) : imageLoadError ? (
                <Typography color="error">
                  Failed to load architecture image: {imageLoadError}
                </Typography>
              ) : null}
            </div>
          </Grid>
        </Grid>
      </InfoCard>
    );
  }

  if (!shouldRenderManualGraph) {
    return (
      <EmptyState
        missing="info"
        title="No architecture available for this entity"
        description="Add amfs.io/architecture-diagram-url to show an architecture image in this tab."
      />
    );
  }

  return (
    <InfoCard title={title}>
      <Grid container>
        <Grid item xs={12} className={classes.legendRow}>
          {manualFlow.protocols.map(protocol => (
            <Chip
              key={protocol}
              label={protocol}
              size="small"
              className={classes.legendChip}
            />
          ))}
        </Grid>
        <Grid item xs={12}>
          <div className={classes.flowRoot}>
            <ReactFlow
              nodes={manualFlow.nodes}
              edges={manualFlow.edges}
              fitView
              fitViewOptions={{ padding: 0.24, minZoom: 0.5 }}
            >
              <Background gap={18} size={1} color="#1e293b" />
              <MiniMap pannable zoomable />
              <Controls />
            </ReactFlow>
          </div>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
